import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '../Layout/MainLayout';
import SaleWorkflowExecutionsSF from './SaleWorkflowExecutionsSF';
import SaleApiLogsViewerSF from './SaleApiLogsViewerSF';

/**
 * SaleDebugDashboardSF - Debug dashboard for Salesforce Sale Order Agent
 * Shows workflow executions and detailed API logs
 */
const SaleDebugDashboardSF = () => {
  const [currentView, setCurrentView] = useState('executions');
  const [selectedExecutionId, setSelectedExecutionId] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Check URL params for direct execution access
    const searchParams = new URLSearchParams(location.search);
    const execution = searchParams.get('execution');

    if (execution) {
      setSelectedExecutionId(execution);
      setCurrentView('api-logs');
    }
  }, [location.search]);

  const showApiLogs = (executionId) => {
    setSelectedExecutionId(executionId);
    setCurrentView('api-logs');
  };

  const backToExecutions = () => {
    setCurrentView('executions');
    setSelectedExecutionId(null);
  };

  return (
    <MainLayout title="Salesforce Sale Order Debug Center">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              {selectedExecutionId
                ? `Debug: ${selectedExecutionId.substring(0, 8)}...`
                : 'Salesforce Sale Order Debug Center'}
            </h1>
          </div>
          <p className="text-gray-600 text-lg">
            Debug Salesforce Sale Order workflow executions and analyze API calls
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-center mb-8">
          <nav className="flex space-x-1 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setCurrentView('executions')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                currentView === 'executions'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>Workflow Executions</span>
            </button>
            {selectedExecutionId && (
              <button
                onClick={() => setCurrentView('api-logs')}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentView === 'api-logs'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>API Logs</span>
                <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                  {selectedExecutionId.substring(0, 8)}...
                </span>
              </button>
            )}
          </nav>
        </div>

        {/* Content */}
        {currentView === 'executions' && (
          <SaleWorkflowExecutionsSF onShowApiLogs={showApiLogs} />
        )}

        {currentView === 'api-logs' && selectedExecutionId && (
          <SaleApiLogsViewerSF
            executionId={selectedExecutionId}
            onClose={backToExecutions}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default SaleDebugDashboardSF;
